import React from "react";

import campaignStepOneIcon from "../../assets/images/svg/mab-compgn-2.svg";
import dropOpenIcon from "../../assets/images/svg/mab-drop-open-2.svg";
import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import videoFrame from '../../assets/images/svg/video-frame.svg'
import NextButton from "../common/next-button";

type VideoItem = {
  id: string;
  fileName: string;
  file?: File;
  isUploading: boolean;
  uploadProgress: number;
  facebookVideoId?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
};

type VideoDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  videos: VideoItem[];
  onVideoUploadChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveVideo: (videoId: string) => void;
  isEditMode?: boolean;
  selectedVideoId?: string | null;
  onVideoSelect?: (videoId: string) => void;
};

const VideoDropdown: React.FC<VideoDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  videos,
  onVideoUploadChange,
  onRemoveVideo,
  isEditMode = false,
  selectedVideoId,
  onVideoSelect,
}) => {
  const isOpen = expandedId === id;
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dragCounterRef = React.useRef(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const MAX_VIDEOS = 5;
  const hasVideos = videos.length > 0;
  const canAddMore = videos.length < MAX_VIDEOS;
  const isAnyUploading = videos.some(v => v.isUploading);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only process if files are actually selected
    if (e.target.files && e.target.files.length > 0) {
      onVideoUploadChange(e);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleVideoClick = (videoId: string) => {
    if (onVideoSelect && !isAnyUploading) {
      onVideoSelect(videoId);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddMore && !isEditMode) {
      dragCounterRef.current++;
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canAddMore && !isEditMode) {
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    if (!canAddMore || isEditMode) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Create a synthetic event with the dropped files
      const syntheticEvent = {
        target: { files },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onVideoUploadChange(syntheticEvent);
    }
  };

  return (
    <div className="w-full">
      <div
        className="relative rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden"
        style={{
          borderRadius: "20px",
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity"
          style={{
            height: "88px",
            padding: "24px",
          }}
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-3 flex-1">
            <img src={isOpen ? dropOpenIcon : campaignStepOneIcon} alt="Step 4" className="w-8 h-8" />
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: "20px",
                lineHeight: "130%",
                letterSpacing: "0%",
                textAlign: "center",
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Ad Creative
            </span>
          </div>
          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform"
            style={{
              height: "9.5px",
              width: "auto",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {isOpen && (
          <div
            className="flex flex-col gap-4 pb-8 ml-10"
            style={{
              paddingLeft: "24px",
              paddingRight: "24px",
            }}
          >
            <div className="flex flex-col gap-2">
              <span className="text-[16px] font-normal leading-[130%]" style={{ color: "#E9EEF5" }}>
                Upload your ad video(s)!
              </span>
              <span style={{ color: "#8FA5BF", fontSize: "14px", fontWeight: 500, lineHeight: "150%" }}>
                Upload up to {MAX_VIDEOS} videos (15 - 60 seconds) here. {videos.length > 0 && `(${videos.length}/${MAX_VIDEOS} uploaded)`}
              </span>
            </div>

            {/* Drop zone - show when can add more videos */}
            {canAddMore && !isEditMode && (
              <label
                htmlFor="video-upload"
                className="relative w-full min-h-[260px] flex flex-col items-center justify-center cursor-pointer rounded-[14px] border-2 border-dashed transition-all duration-200"
                style={{
                  padding: "32px",
                  backgroundImage: `url(${videoFrame})`,
                  backgroundSize: "100% 100%",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  borderColor: isDragging ? "#58F2A9" : "transparent",
                  backgroundColor: isDragging ? "rgba(88, 242, 169, 0.1)" : "transparent",
                }}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {isDragging && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[rgba(88,242,169,0.15)] rounded-[14px] pointer-events-none">
                    <div className="text-center">
                      <div className="text-[#58F2A9] text-lg font-semibold mb-2 [font-family:'Poppins',Helvetica]">
                        Drop your videos here
                      </div>
                      <div className="text-[#58F2A9] text-sm [font-family:'Poppins',Helvetica]">
                        Release to upload
                      </div>
                    </div>
                  </div>
                )}
              </label>
            )}

            <input
              ref={fileInputRef}
              id="video-upload"
              type="file"
              accept=".mp4,video/mp4,video/*"
              multiple
              className="hidden"
              onChange={handleFileChange}
              disabled={!canAddMore || isEditMode}
            />

            {/* Uploaded videos list */}
            {hasVideos && (
              <div className="flex flex-col gap-3">
                {videos.map((video) => (
                  <div key={video.id} className="w-full rounded-[14px] bg-[#121420] border border-[#2A2F3D] p-3 flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-white text-sm font-medium [font-family:'Poppins',Helvetica]">
                        {video.isUploading ? "Uploading..." : "Uploaded video"}
                      </div>
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={() => onRemoveVideo(video.id)}
                          className="text-xs text-[#EE6161] hover:underline font-medium"
                          disabled={video.isUploading}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {/* Progress bar - only show when uploading */}
                    {video.isUploading && (
                      <div className="w-full h-2 rounded-full bg-[#1E2433] overflow-hidden">
                        <div
                          className="h-full bg-[#58F2A9] transition-all duration-200"
                          style={{ width: `${video.uploadProgress}%` }}
                        />
                      </div>
                    )}

                    {/* Success message - only show when upload is complete */}
                    {!video.isUploading && (
                      <span className="text-[#58F2A9] text-xs [font-family:'Poppins',Helvetica]">
                        ✓ Video uploaded successfully!
                      </span>
                    )}

                    <div className="text-[#878DA1] text-xs [font-family:'Poppins',Helvetica] break-all">
                      {video.fileName}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex w-full justify-end mt-4">
              <NextButton size="sm" onClick={() => onNext && onNext(id)} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoDropdown;

